/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { type AxiosRequestConfig } from "axios";
import * as SuperTokensSession from "supertokens-web-js/recipe/session";
import { Sentry } from "@shared/utils/init-tracking";

import { config } from "@shared/config";
import { toast } from "@shared/hooks/useToaster";
import { attachMaintenanceInterceptor } from "@shared/features/maintenance/maintenance-interceptor";

import { defaultHeaders } from "../../utils";

if (!config.VITE_MC_API) {
  throw new Error("apiUrl is not defined");
}

attachMaintenanceInterceptor(axios);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { status, data } = error.response ?? {};

    console.log("api interceptor error", status, data, error.response);
    Sentry.addBreadcrumb({
      category: "api",
      message: "api interceptor error",
      level: "error",
      data: {
        status,
        data,
        response: error.response,
      },
    });

    switch (status) {
      case 401:
        if (!(await SuperTokensSession.attemptRefreshingSession())) {
          if (window.location.pathname != "/login") {
            console.log("signed out due to 401 and failed refreshing session");
            window.logout()

            return;
          }
        }

        break;

      case 500: {
        console.log("API internal error", status, data, error.response);

        toast({
          title: "Une erreur est survenue",
          description: `Nous sommes désolés de la gêne, veuillez réessayer plus tard. Si le problème persiste, contactez-nous.`,
        });
        break;
      }
    }

    return Promise.reject(error);
  }
);

export const api = attachMaintenanceInterceptor(
  axios.create({
    baseURL: `${config.VITE_MC_API?.replace(/\/+$/, "")}`,
    headers: {
      ...defaultHeaders(),
    },
  })
);

interface DefaultFindParam {
  $first?: number;
  $offset?: number;
}

export class BaseService<
  Model = any,
  FindParam = DefaultFindParam & Partial<Model>,
  CreateDTO = Model,
  UpdateDTO = Partial<Model>,
  CreateResult = Model,
  FindResult = Model[]
> {
  public api: ReturnType<typeof axios['create']>;

  constructor(path: string = "") {
    this.api = axios.create({
      baseURL: `${config.VITE_MC_API.replace(/\/+$/, "")}/${path.replace(
        /^\/+/,
        ""
      )}`,
      headers: {
        ...defaultHeaders(),
      },
    });

    attachMaintenanceInterceptor(this.api);
  }

  protected _get(url: string, config: AxiosRequestConfig = {}): Promise<any> {
    return this.api.get(url, config).then((res) => res.data);
  }

  protected _post(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {}
  ): Promise<any> {
    return this.api.post(url, data, config).then((res) => res.data);
  }

  protected _put(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {}
  ): Promise<any> {
    return this.api.put(url, data, config).then((res) => res.data);
  }

  protected _delete(
    url: string,
    config: AxiosRequestConfig = {}
  ): Promise<any> {
    return this.api.delete(url, config).then((res) => res.data);
  }

  protected _patch(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {}
  ): Promise<any> {
    return this.api.patch(url, data, config).then((res) => res.data);
  }

  protected _request(config: AxiosRequestConfig): Promise<any> {
    return this.api.request(config).then((res) => res.data);
  }

  public get(id: string | number): Promise<Model> {
    return this._get(`/${id}`);
  }

  public create(data: CreateDTO): Promise<CreateResult> {
    return this._post("/", data);
  }

  public update(id: string | number, data: UpdateDTO): Promise<Model> {
    return this._patch(`/${id}`, data);
  }

  public delete(id: string | number): Promise<Model> {
    return this._delete(`/${id}`);
  }

  public find(params: FindParam = {} as FindParam): Promise<FindResult> {
    return this._get("/", { params });
  }

  public findOne(
    params: FindParam = {} as FindParam
  ): Promise<Model[]> | Promise<Model> {
    return this._get("/", { params: { ...params, $first: 1 } }).then(
      (r) => r?.[0]
    );
  }
}
